import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import { lookupCrypto, type CryptoResult } from "@/lib/api";
import { IMG_BASE } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

const POPULAR_CRYPTO = [
  { symbol: "btc", name: "Bitcoin" },
  { symbol: "eth", name: "Ethereum" },
  { symbol: "sol", name: "Solana" },
  { symbol: "ada", name: "Cardano" },
  { symbol: "doge", name: "Dogecoin" },
  { symbol: "xrp", name: "XRP" },
  { symbol: "dot", name: "Polkadot" },
  { symbol: "avax", name: "Avalanche" },
  { symbol: "matic", name: "Polygon" },
  { symbol: "link", name: "Chainlink" },
] as const;

interface CryptoPageProps {
  serverData?: CryptoResult | null;
}

export default function CryptoPage({ serverData }: CryptoPageProps) {
  const { symbol } = useParams<{ symbol: string }>();
  const [crypto, setCrypto] = useState<CryptoResult | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);

  const lowerSymbol = symbol?.toLowerCase() || "";

  useEffect(() => {
    if (serverData) return;
    if (!symbol) return;
    setLoading(true);
    setCrypto(null);
    lookupCrypto(symbol)
      .then(setCrypto)
      .catch(() => setCrypto({ error: "Failed to load crypto data" } as CryptoResult))
      .finally(() => setLoading(false));
  }, [symbol, serverData]);

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Looking up {lowerSymbol.toUpperCase()}...</p>
      </div>
    );
  }

  if (!crypto || crypto.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Token not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {crypto?.error || `We couldn't find data for crypto token "${lowerSymbol.toUpperCase()}".`}
          </p>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Crypto", to: "/" },
        { label: crypto.symbol?.toUpperCase() || lowerSymbol.toUpperCase() },
      ]}
    >
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={crypto.logo || `${IMG_BASE}/crypto/${lowerSymbol}?size=256`}
              alt={`${crypto.name} logo`}
              className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => {
                (e.target as HTMLElement).style.opacity = "0.3";
              }}
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                {crypto.symbol?.toUpperCase() || lowerSymbol.toUpperCase()}
              </h1>
              {crypto.chain && <Badge variant="secondary">{crypto.chain}</Badge>}
            </div>
            <p className="text-lg text-secondary-foreground">{crypto.name}</p>
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/crypto/${lowerSymbol}" />`} />
      </section>

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Crypto logo -->\n<img src="${IMG_BASE}/crypto/${lowerSymbol}" />\n\n<!-- With size -->\n<img src="${IMG_BASE}/crypto/${lowerSymbol}?size=128" />\n\n<!-- Crypto lookup API (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/crypto/${lowerSymbol}")\n</script>`} />
      </section>

      {/* Related Crypto */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular Tokens</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {POPULAR_CRYPTO.filter((t) => t.symbol !== lowerSymbol).map((t) => (
            <Link
              key={t.symbol}
              to={`/crypto/${t.symbol}`}
              className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <img
                src={`${IMG_BASE}/crypto/${t.symbol}?size=64`}
                alt={t.symbol}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
              <div className="min-w-0">
                <div className="text-sm font-bold">{t.symbol.toUpperCase()}</div>
                <div className="text-[11px] text-muted-foreground truncate">{t.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
